import React from 'react';
import { motion } from 'framer-motion';
import { BookOpenIcon, CheckCircle2Icon, LockIcon, TabletIcon, ZapIcon } from 'lucide-react';
import { CTAButton } from './Shared';
import { BodySilhouette, DnaHelix, LeafSprig, Mandala, TreeOfLife } from './BioDecor';
const productImage = 'https://cdn.magicpatterns.com/uploads/7HZTNoKfEm9vGA5hFgb1tk/MOCKUP_INGLES.png';
const stats = [{
  icon: TabletIcon,
  text: 'Ready to print or use on a tablet'
}, {
  icon: LockIcon,
  text: 'Secure checkout'
}, {
  icon: ZapIcon,
  text: 'Immediate digital access'
}];
export function Hero() {
  return <header className="relative overflow-hidden bg-blue-950 pb-8 pt-5 text-stone-50 md:pb-20 md:pt-16" data-id="element-129" data-mp="sb1chsl5257m">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden" data-id="element-130" data-mp="ncq13df73mna">
        <TreeOfLife className="absolute -left-8 top-10 h-72 w-44 text-emerald-500/30 md:h-96 md:w-56" data-id="element-131" data-mp="3d1a4l90jrr80" />
        <LeafSprig className="absolute -right-8 top-24 h-72 w-44 -scale-x-100 text-blue-400/25 md:h-[26rem] md:w-56" data-id="element-132" data-mp="381xn73sg3cct" />
        <DnaHelix className="absolute left-[8%] top-[48%] hidden h-56 w-24 text-amber-200/25 lg:block" data-id="element-133" data-mp="3j9k9t8cxxbej" />
        <BodySilhouette className="absolute bottom-8 right-[7%] hidden h-56 w-28 text-blue-400/25 lg:block" data-id="element-134" data-mp="3o6kd5bieo01y" />
        <Mandala className="absolute right-[16%] top-[16%] h-14 w-14 text-amber-400/35 md:h-20 md:w-20" data-id="element-135" data-mp="399tej4gk2sde" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8" data-id="element-136" data-mp="3e7l8s54oaqkr">
        <motion.div initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-137" data-mp="30ggfdkrweuiv">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber-500/50 bg-white/10 px-3 py-1 md:mb-4 md:px-4 md:py-1.5" data-id="element-138" data-mp="2qloeo830rrjv">
            <span className="text-[10px] font-bold tracking-[.18em] text-amber-200 md:text-xs" data-id="element-139" data-mp="2hincujl6smm0">
              LUMEN AKASHA™
            </span>
          </div>

          <div className="mx-auto max-w-5xl" data-id="element-140" data-mp="2lmv4aoj14gnx">
            <h1 className="text-[26px] font-bold leading-[1.1] tracking-tight text-white md:text-[clamp(2.4rem,6.4vw,4.8rem)] md:leading-[1.02] md:tracking-[-0.035em]" data-id="element-141" data-mp="s2b6puz5ad3k">
              <span className="block" data-id="element-142" data-mp="ic889f9986xu">More structured,</span>
              <span className="block text-amber-200" data-id="element-143" data-mp="icrs51igt5ei">more confident sessions</span>
              <span className="block" data-id="element-144" data-mp="degeefp1uar0">— without improvising.</span>
            </h1>
            <div className="mx-auto mt-3.5 h-0.5 w-32 rounded-full bg-amber-300 md:mt-5 md:w-64" aria-hidden="true" data-id="element-145" data-mp="1b15prg5n5zpj" />
            <p className="mx-auto mt-3.5 max-w-2xl text-[13px] font-medium leading-relaxed text-stone-50/80 md:mt-5 md:text-lg" data-id="element-146" data-mp="16345irwhbuch">
              You already have the knowledge. Now you can have the system to apply it with greater structure,
              clarity, and professional judgement.
            </p>
          </div>

          <div className="mx-auto mt-4 flex max-w-2xl justify-center md:mt-10" data-id="element-147" data-mp="3lu9tqr7n9g6h">
            <motion.img src={productImage} alt="Biodecoding Practitioner Kit: the spiral-bound main manual surrounded by its session cards, maps and practitioner resources" initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.3,
            ease: [0.23, 1, 0.32, 1]
          }} className="h-auto w-[82%] max-w-[300px] drop-shadow-2xl sm:w-[80%] sm:max-w-[380px] md:w-[94%] md:max-w-[600px]" draggable={false} fetchPriority="high" data-id="element-148" data-mp="3hpsdv34enn0z" />
          </div>

          <div className="mx-auto mt-4 max-w-3xl space-y-2.5 md:mt-6 md:space-y-3" data-id="element-149" data-mp="2ky4352wttj9t">
            <h2 className="text-[15px] font-bold leading-snug tracking-normal text-amber-200 md:text-3xl md:tracking-wide" data-id="element-150" data-mp="2ozz4g7erbwnl">
              🧬 BIODECODING PRACTITIONER KIT™
            </h2>
            <div className="mx-auto h-px max-w-xs bg-amber-500/60" aria-hidden="true" data-id="element-151" data-mp="umpp3yzj5gxf" />
            
            <p className="mx-auto max-w-2xl text-[13px] font-medium leading-relaxed text-stone-50/85 md:text-lg" data-id="element-152" data-mp="zlappzip22me">
              A complete professional toolkit of session frameworks, integration practices, guided questions, and
              practitioner resources — ready to consult in your very next session.
            </p>
            <p className="mx-auto max-w-2xl rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-[11px] font-medium leading-relaxed text-stone-50/75 md:text-sm" data-id="element-153" data-mp="e8im3ojc8l9n">
              📘  Educational material for practitioners and facilitators. Tools for organisation, exploration, reflection, and client support within your professional scope.
            </p>
            <CTAButton variant="gold" className="mt-2 px-6 py-3 text-sm md:mt-3 md:px-10 md:py-5 md:text-xl" data-id="element-154" data-mp="3rg3ldrql2y6a">
              → VIEW THE KIT
            </CTAButton>
          </div>

          <div className="mx-auto mt-6 grid max-w-3xl gap-3 rounded-2xl border border-white/15 bg-white/5 p-4 sm:grid-cols-3 md:mt-10 md:p-5" data-id="element-155" data-mp="3mnahf62xvtv9">
            {stats.map(stat => {
            const Icon = stat.icon;
            return <div key={stat.text} className="flex flex-col items-center gap-2 text-center" data-id="element-156" data-mp="19inky88tv32s">
                  <Icon className="h-6 w-6 text-amber-400" data-id="element-157" data-mp="1e9dnldpvcrts" />
                  <span className="text-xs leading-snug text-stone-50/80 md:text-sm" data-id="element-158" data-mp="1eayhr289u8r4">{stat.text}</span>
                </div>;
          })}
          </div>

          <div className="mt-4 flex flex-wrap justify-center gap-2 text-[10px] font-semibold md:mt-6 md:text-sm" data-id="element-159" data-mp="14jawjj2b5ny3">
            <span className="rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-stone-50/85" data-id="element-160" data-mp="1s8vhg7i0uva0">
              <BookOpenIcon className="mr-1.5 inline h-3.5 w-3.5 text-amber-400" data-id="element-161" data-mp="1x76rojc8b7sr" />
              PDF format
            </span>
            <span className="rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-stone-50/85" data-id="element-162" data-mp="1j7xfrvv22zul">
              <CheckCircle2Icon className="mr-1.5 inline h-3.5 w-3.5 text-amber-400" data-id="element-163" data-mp="1n88y31zy4o2w" />
              7-day guarantee
            </span>
          </div>
        </motion.div>
      </div>
    </header>;
}